import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { Autocomplete } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'util';
import { IUser } from "../../../models/IUser";
import UserAdminServiceInstance from '../../../services/UserAdminService';
import IAdminTag from '../../../models/IAdminTag';

interface ITagErrors {
    tagNumber?: string;
    assignedTo?: string;
    pin?: string;
}
interface IEditTagDialogProps {
    tag: IAdminTag;
    onCompleteEdit: (tag: IAdminTag) => void;
    onCancelEdit: () => void;
}

function EditTagDialog(props: IEditTagDialogProps) {
    const { t } = useTranslation("common");
    const [currentTag, setCurrentTag] = useState<IAdminTag>(props.tag);
    const [errors, setErrors] = React.useState<ITagErrors>();
    const [open, setOpen] = React.useState(false);
    const [users, setUsers] = React.useState<IUser[]>([]);
    const [tagSearchTerm, setTagSearchTerm] = React.useState<string>();
    const loading = open && users.length === 0;
    
    React.useEffect(() => {
        if (!open) {
            setUsers([]);
        }
    }, [open]);

    const handleClose = () => {
        props.onCancelEdit();
    }

    const handleCompleteEdit = () => {
        props.onCompleteEdit(currentTag);
        handleClose();
    }

    const checkTag = (tagToValidate: IAdminTag) => {
        const error: ITagErrors = {};
        let anyError = false;
        if (tagToValidate) {
            if (tagToValidate.pin.length === 0) {
                error.pin = format(t("commonErrors.requiredField"), t("adminTagManagement.pin"));
                anyError = true;
            } else if (tagToValidate.pin.length !== 4) {
                error.pin = format(t("commonErrors.invalidField"), t("adminTagManagement.pin"));
            }
        }
        if (anyError === true) {
            setErrors(error);
        } else {
            setErrors(undefined);
        }
    }

    useEffect(() => {
        const timerId = setTimeout(() => {
            if (tagSearchTerm) {
                search(tagSearchTerm);
            }
        }, 500);
        return () => clearTimeout(timerId);

        function search(term: string) {
            if (term && term.length > 2) {
                UserAdminServiceInstance.searchUsers(term).then(response => {
                    setUsers(response);
                }).catch(reason => console.error(reason));
            }
        }
    }, [tagSearchTerm])


    return (
        <Dialog open={true} aria-labelledby="form-dialog-title">
            <DialogTitle id="form-dialog-title">{t("adminTagManagement.dialogTitle")}</DialogTitle>
            <DialogContent>

                <TextField
                    autoFocus
                    error={Boolean(errors?.tagNumber)}
                    helperText={errors?.tagNumber}
                    margin="dense"
                    id="tagNumber"
                    label={t("readersSetup.readerId")}
                    type="text"
                    fullWidth
                    value={currentTag.tagNumber}
                    onChange={e => {
                        const newTag: IAdminTag = { ...currentTag, tagNumber: e.target.value };
                        setCurrentTag(newTag);
                        checkTag(newTag);
                    }}
                />
                <TextField
                    error={Boolean(errors?.pin)}
                    helperText={errors?.pin}
                    margin="dense"
                    id="pin"
                    label={t("adminTagManagement.pin")}
                    type="string"
                    value={currentTag.pin}
                    onChange={e => {
                        const newTag: IAdminTag = { ...currentTag, pin: e.target.value };
                        setCurrentTag(newTag);
                        checkTag(newTag);
                    }}
                />
                <Autocomplete
                    id="assignedTo"
                    filterOptions={(x) => x}
                    isOptionEqualToValue={(option, value) => option.userId === value.userId}
                    getOptionLabel={(option) => option.name}
                    options={users}
                    loading={loading}
                    loadingText={t("commonLabels.loading")}
                    onChange={(event, value) => {
                        const newTag: IAdminTag = { ...currentTag, assignedToId: value?.userId };
                        setCurrentTag(newTag);
                        checkTag(newTag);
                    }}
                    open={open}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    onClose={() => {
                        setOpen(false);
                    }}
                    onInputChange={(e, value, reason) => setTagSearchTerm(value)}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t("readersSetup.assignedTo")}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <React.Fragment>
                                        {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                        {params.InputProps.endAdornment}
                                    </React.Fragment>
                                )
                            }}
                        />
                    )}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCompleteEdit} color="primary" disabled={errors !== undefined}>
                    {t("commonActions.ok")}
                </Button>
                <Button onClick={handleClose} color="primary">
                    {t("commonActions.cancel")}
                </Button>
            </DialogActions>
        </Dialog >
    );
}

export default EditTagDialog;